/**
 * Shared date/time/recurrence/modality/location/room/equipment fields used by
 * the Calendar Group tab and Group create Stage 2 (series schedule).
 */
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePicker, DatePickerInput } from '@mantine/dates';
import { notify } from '@/lib/notify';
import {
  IconBuilding,
  IconCalendarEvent,
  IconClock,
  IconCopy,
} from '@tabler/icons-react';
import { useMemo, type ReactNode } from 'react';

import { formatDateOnlyLocal, parseDateOnlyLocal } from '@/lib/dateHelpers';
import { applyZonedWallClock } from '@/lib/unifyDateFormat';
import { TIME_SLOT_OPTIONS, parseTimeLabel12h } from '@/features/calendar/providerAvailabilityValidation';
import { DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE } from '@unify/fhir-model/resources/schedule';
import type { LocationWeeklyHours } from '@unify/fhir-model/resources/location';
import type { Equipment } from '@/pages/settings/components/EquipmentManagementSection';
import type { Room } from '@/pages/settings/components/RoomManagementSection';

import { RoomSelect } from './RoomSelect';
import type { MinuteInterval } from './roomSchedulingDemoData';

const BORDER_COLOR = 'var(--mantine-color-gray-5)';

function startOfTodayLocal(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export type GroupScheduleModality = 'in-person' | 'telehealth' | 'hybrid';

export type GroupAppointmentScheduleFieldsValue = {
  date: Date | null;
  startTime: string;
  endTime: string;
  recurringEnabled: boolean;
  /** When `enableCustomDates` is on: recurring pattern vs pick explicit dates. */
  scheduleMode: 'recurring' | 'custom';
  /** YYYY-MM-DD strings when scheduleMode is custom (matches GroupSetupFormValues.sessionDates). */
  sessionDates: string[];
  recurringInterval: number;
  recurringTimeframe: 'week' | 'month';
  recurringDaysOfWeek: string[];
  recurringEndType: 'occurrences' | 'date';
  recurringEndDate: Date | null;
  recurringOccurrences: number;
  modality: GroupScheduleModality;
  /** Practice site id, or `telehealth` sentinel when modality is telehealth. */
  locationId: string;
  telehealthFromOffice: boolean;
  officeForTelehealth: string;
  roomId: string | null;
  deviceIds: string[];
};

export type GroupAppointmentScheduleSite = {
  id: string;
  name: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  hours?: LocationWeeklyHours;
};

export type GroupAppointmentScheduleFieldsProps = {
  value: GroupAppointmentScheduleFieldsValue;
  onChange: (patch: Partial<GroupAppointmentScheduleFieldsValue>) => void;
  sites: GroupAppointmentScheduleSite[];
  siteOptions: { value: string; label: string }[];
  firstSiteId: string;
  schedulingRooms: Room[];
  portableDevices: Equipment[];
  fixedInRoom: Equipment[];
  busyByRoom?: Map<string, MinuteInterval[]>;
  busyByDevice?: Map<string, MinuteInterval[]>;
  conflictedRoomIds?: Set<string>;
  conflictedDeviceIds?: Set<string>;
  availabilityLoading?: boolean;
  /** Hide Recurring checkbox (e.g. editing an existing calendar event). */
  hideRecurringCheckbox?: boolean;
  /** Show the "this is a recurring event" alert above the recurrence panel. */
  showRecurringEditAlert?: boolean;
  /** Force the recurrence editor open (e.g. editing a recurring calendar event). */
  forceShowRecurrencePanel?: boolean;
  /**
   * Group series Stage 2 layout: "Start Date", times on their own row,
   * optional enrollment lock, and Recurring | Select dates modes.
   */
  groupSeriesLayout?: boolean;
  showEnrollmentLockDate?: boolean;
  enrollmentLockDate?: Date | null;
  onEnrollmentLockDateChange?: (date: Date | null) => void;
  enrollmentLockDateError?: string;
  onViewRoomAvailability?: () => void;
  /** Optional content rendered after equipment (calendar-only hooks). */
  footer?: ReactNode;
  displayTimeZone?: string;
};

export function GroupAppointmentScheduleFields({
  value,
  onChange,
  sites,
  siteOptions,
  firstSiteId,
  schedulingRooms,
  portableDevices: _portableDevices,
  fixedInRoom: _fixedInRoom,
  busyByRoom,
  busyByDevice: _busyByDevice,
  conflictedRoomIds,
  conflictedDeviceIds: _conflictedDeviceIds,
  availabilityLoading,
  hideRecurringCheckbox = false,
  showRecurringEditAlert = false,
  forceShowRecurrencePanel = false,
  groupSeriesLayout = false,
  showEnrollmentLockDate = false,
  enrollmentLockDate = null,
  onEnrollmentLockDateChange,
  enrollmentLockDateError,
  onViewRoomAvailability,
  footer,
  displayTimeZone = DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
}: GroupAppointmentScheduleFieldsProps): JSX.Element {
  const isCustomDates =
    groupSeriesLayout && value.scheduleMode === 'custom';
  const showRecurrencePanel =
    forceShowRecurrencePanel
    || (groupSeriesLayout
      ? value.scheduleMode === 'recurring'
      : value.recurringEnabled);
  const patch = (partial: Partial<GroupAppointmentScheduleFieldsValue>) => onChange(partial);
  const today = startOfTodayLocal();
  const schedulingSiteId =
    value.modality === 'telehealth' ? value.officeForTelehealth : value.locationId;
  const siteHours = sites.find((s) => s.id === schedulingSiteId)?.hours;
  const { bookingStart, bookingEnd } = useMemo(() => {
    const s = parseTimeLabel12h(value.startTime);
    const e = parseTimeLabel12h(value.endTime);
    if (!value.date || !s || !e) return { bookingStart: null, bookingEnd: null };
    const start = applyZonedWallClock(value.date, s.hour, s.minute, displayTimeZone);
    const end = applyZonedWallClock(value.date, e.hour, e.minute, displayTimeZone);
    if (end <= start) return { bookingStart: null, bookingEnd: null };
    return { bookingStart: start, bookingEnd: end };
  }, [value.date, value.startTime, value.endTime, displayTimeZone]);

  const parsePickerDate = (next: string | Date | null): Date | null => {
    if (next == null) return null;
    if (typeof next === 'string') {
      return parseDateOnlyLocal(next.slice(0, 10)) ?? new Date(next);
    }
    return next;
  };

  const setStartDate = (next: Date | null) => {
    const updates: Partial<GroupAppointmentScheduleFieldsValue> = { date: next };
    if (
      next
      && value.recurringEndDate
      && value.recurringEndDate.getTime() < next.getTime()
    ) {
      updates.recurringEndDate = next;
    }
    patch(updates);
    if (
      next
      && enrollmentLockDate
      && enrollmentLockDate.getTime() > next.getTime()
    ) {
      onEnrollmentLockDateChange?.(next);
    }
  };

  return (
    <Stack gap="md">
      {groupSeriesLayout ? (
        <>
          <Grid>
            <Grid.Col span={showEnrollmentLockDate ? 6 : 12}>
              <DatePickerInput
                label="Start Date"
                value={value.date}
                onChange={(next) => setStartDate(parsePickerDate(next))}
                minDate={today}
                leftSection={<IconCalendarEvent size={16} />}
              />
            </Grid.Col>
            {showEnrollmentLockDate && (
              <Grid.Col span={6}>
                <DatePickerInput
                  label="Enrollment Lock Date"
                  value={enrollmentLockDate}
                  onChange={(next) =>
                    onEnrollmentLockDateChange?.(parsePickerDate(next))
                  }
                  minDate={today}
                  maxDate={value.date ?? undefined}
                  leftSection={<IconCalendarEvent size={16} />}
                  error={enrollmentLockDateError}
                />
              </Grid.Col>
            )}
          </Grid>
          <Grid>
            <Grid.Col span={6}>
              <Select
                label="Start time"
                value={value.startTime}
                onChange={(v) => patch({ startTime: v ?? '9:00 AM' })}
                data={TIME_SLOT_OPTIONS}
                leftSection={<IconClock size={16} />}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Select
                label="End time"
                value={value.endTime}
                onChange={(v) => patch({ endTime: v ?? '10:00 AM' })}
                data={TIME_SLOT_OPTIONS}
                leftSection={<IconClock size={16} />}
              />
            </Grid.Col>
          </Grid>
        </>
      ) : (
        <Grid>
          <Grid.Col span={4}>
            <DatePickerInput
              label="Date"
              value={value.date}
              onChange={(next) => patch({ date: parsePickerDate(next) })}
              leftSection={<IconCalendarEvent size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <Select
              label="Start time"
              value={value.startTime}
              onChange={(v) => patch({ startTime: v ?? '9:00 AM' })}
              data={TIME_SLOT_OPTIONS}
              leftSection={<IconClock size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <Select
              label="End time"
              value={value.endTime}
              onChange={(v) => patch({ endTime: v ?? '10:00 AM' })}
              data={TIME_SLOT_OPTIONS}
              leftSection={<IconClock size={16} />}
            />
          </Grid.Col>
        </Grid>
      )}

      {groupSeriesLayout ? (
        <SegmentedControl
          fullWidth
          value={value.scheduleMode}
          onChange={(v) => {
            const mode = v as 'recurring' | 'custom';
            patch({
              scheduleMode: mode,
              recurringEnabled: mode === 'recurring',
              ...(mode === 'recurring' && value.date
                ? { recurringDaysOfWeek: [String(value.date.getDay())] }
                : {}),
              ...(mode === 'custom'
                && value.date
                && value.sessionDates.length === 0
                ? { sessionDates: [formatDateOnlyLocal(value.date)] }
                : {}),
            });
          }}
          data={[
            { label: 'Recurring schedule', value: 'recurring' },
            { label: 'Select dates', value: 'custom' },
          ]}
          mt="sm"
          mb="sm"
        />
      ) : (
        !hideRecurringCheckbox && (
          <Checkbox
            label="Recurring"
            checked={value.recurringEnabled}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              patch({
                recurringEnabled: checked,
                scheduleMode: checked ? 'recurring' : 'custom',
                ...(checked && value.date
                  ? { recurringDaysOfWeek: [String(value.date.getDay())] }
                  : {}),
              });
            }}
            size="sm"
          />
        )
      )}

      {showRecurringEditAlert && (
        <Alert color="blue" variant="light" style={{ borderColor: BORDER_COLOR }}>
          <Text size="sm">
            This is a recurring event. You can edit the schedule below or modify just this
            instance.
          </Text>
        </Alert>
      )}

      {isCustomDates && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Session dates
          </Text>
          <Text size="xs" c="dimmed">
            Pick each session day on the calendar.
          </Text>
          <Paper withBorder p="sm" radius="md" w="fit-content" maw="100%">
            <DatePicker
              type="multiple"
              value={value.sessionDates}
              minDate={today}
              onChange={(dates) => {
                const next = (dates ?? [])
                  .map((d) =>
                    typeof d === 'string'
                      ? d.slice(0, 10)
                      : formatDateOnlyLocal(d),
                  )
                  .filter(Boolean)
                  .sort();
                const first = next[0] ? parseDateOnlyLocal(next[0]) : null;
                patch({
                  sessionDates: next,
                  date: first ?? value.date,
                });
              }}
              data-testid="group-session-dates-picker"
            />
          </Paper>
          {(value.sessionDates.length ?? 0) > 0 && (
            <Text size="xs" c="dimmed">
              {value.sessionDates.length} date
              {value.sessionDates.length === 1 ? '' : 's'} selected
              {value.sessionDates.length <= 8
                ? `: ${value.sessionDates.join(', ')}`
                : ''}
            </Text>
          )}
        </Stack>
      )}

      {showRecurrencePanel && (
        <Paper
          withBorder
          p="md"
          style={{ borderColor: BORDER_COLOR, backgroundColor: 'var(--mantine-color-gray-0)' }}
        >
          <Stack gap="sm">
            <Group gap="xs" align="center">
              <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                Every
              </Text>
              <NumberInput
                value={value.recurringInterval}
                onChange={(v) => patch({ recurringInterval: Number(v) || 1 })}
                min={1}
                max={30}
                style={{ width: 70 }}
                size="sm"
              />
              <Select
                value={value.recurringTimeframe}
                onChange={(v) =>
                  patch({ recurringTimeframe: (v as 'week' | 'month') ?? 'week' })
                }
                data={[
                  { value: 'week', label: 'week' },
                  { value: 'month', label: 'month' },
                ]}
                style={{ width: 100 }}
                size="sm"
              />
            </Group>
            {value.recurringTimeframe === 'week' && (
              <Group gap="xs" align="center">
                <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                  On day(s)
                </Text>
                <Group gap={4}>
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => {
                    const isSelected = value.recurringDaysOfWeek.includes(String(idx));
                    return (
                      <Button
                        key={idx}
                        variant={isSelected ? 'filled' : 'outline'}
                        color="gold"
                        size="sm"
                        onClick={() => {
                          if (isSelected) {
                            patch({
                              recurringDaysOfWeek: value.recurringDaysOfWeek.filter(
                                (d) => d !== String(idx),
                              ),
                            });
                          } else {
                            patch({
                              recurringDaysOfWeek: [
                                ...value.recurringDaysOfWeek,
                                String(idx),
                              ],
                            });
                          }
                        }}
                        style={{ width: 32, height: 32, padding: 0, minWidth: 32 }}
                      >
                        {day}
                      </Button>
                    );
                  })}
                </Group>
              </Group>
            )}
            <Group gap="xs" align="center">
              <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                Ends
              </Text>
              <SegmentedControl
                value={value.recurringEndType}
                onChange={(v) =>
                  patch({ recurringEndType: v as 'occurrences' | 'date' })
                }
                data={[
                  { label: 'After', value: 'occurrences' },
                  { label: 'On', value: 'date' },
                ]}
                size="sm"
              />
              {value.recurringEndType === 'occurrences' && (
                <Group gap="xs" align="center">
                  <NumberInput
                    value={value.recurringOccurrences}
                    onChange={(v) => patch({ recurringOccurrences: Number(v) || 1 })}
                    min={1}
                    max={365}
                    size="sm"
                    style={{ width: 70 }}
                  />
                  <Text size="sm" c="gray.7">
                    occurrence(s)
                  </Text>
                </Group>
              )}
              {value.recurringEndType === 'date' && (
                <DatePickerInput
                  value={value.recurringEndDate}
                  onChange={(v) =>
                    patch({ recurringEndDate: parsePickerDate(v) })
                  }
                  size="sm"
                  style={{ width: 150 }}
                  minDate={value.date ?? (groupSeriesLayout ? today : undefined)}
                />
              )}
            </Group>
          </Stack>
        </Paper>
      )}

      <Stack gap="sm">
        <Grid>
          <Grid.Col span={value.modality === 'telehealth' ? 12 : 6}>
            <Select
              label="Modality"
              value={value.modality}
              onChange={(v) => {
                const next = (v ?? 'in-person') as GroupScheduleModality;
                if (next === 'telehealth') {
                  patch({
                    modality: next,
                    locationId: 'telehealth',
                    roomId: null,
                    deviceIds: [],
                  });
                } else {
                  patch({
                    modality: next,
                    telehealthFromOffice: false,
                    locationId:
                      value.locationId === 'telehealth' ? firstSiteId : value.locationId,
                  });
                }
              }}
              data={[
                { value: 'in-person', label: 'In-person' },
                { value: 'telehealth', label: 'Telehealth' },
                { value: 'hybrid', label: 'Hybrid' },
              ]}
              leftSection={<IconBuilding size={16} />}
            />
          </Grid.Col>
          {value.modality !== 'telehealth' && (
            <Grid.Col span={6}>
              <Group align="flex-end" gap="xs">
                <Box style={{ flex: 1 }}>
                  <Select
                    label="Location"
                    value={value.locationId}
                    onChange={(v) =>
                      patch({
                        locationId: v ?? firstSiteId,
                        roomId: null,
                        deviceIds: [],
                      })
                    }
                    data={siteOptions}
                    leftSection={<IconBuilding size={16} />}
                  />
                </Box>
                <Tooltip label="Copy address">
                  <ActionIcon
                    size="lg"
                    variant="light"
                    color="gray"
                    onClick={() => {
                      const site = sites.find((s) => s.id === value.locationId);
                      const addr = site
                        ? [site.addressLine1, site.city, site.state].filter(Boolean).join(', ')
                        : '';
                      void navigator.clipboard?.writeText(addr).then(() =>
                        notify({
                          title: 'Copied',
                          message: 'Address copied',
                          type: 'success',
                        }),
                      );
                    }}
                  >
                    <IconCopy size={18} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Grid.Col>
          )}
        </Grid>

        {value.modality === 'telehealth' && (
          <Switch
            label="Taking call from office (reserve a phone booth)"
            checked={value.telehealthFromOffice}
            onChange={(e) => {
              const checked = e.currentTarget.checked;
              patch({
                telehealthFromOffice: checked,
                ...(!checked ? { roomId: null, deviceIds: [] } : {}),
              });
            }}
          />
        )}
        {value.modality === 'telehealth' && value.telehealthFromOffice && (
          <Select
            label="Office"
            value={value.officeForTelehealth}
            onChange={(v) =>
              patch({
                officeForTelehealth: v ?? firstSiteId,
                roomId: null,
                deviceIds: [],
              })
            }
            data={siteOptions}
            leftSection={<IconBuilding size={16} />}
          />
        )}

        {(value.modality === 'in-person' || value.modality === 'hybrid')
          && value.locationId !== 'telehealth' && (
          <Stack gap="xs">
            <RoomSelect
              rooms={schedulingRooms.filter((r) => r.type === 'therapy-room')}
              eventDate={value.date}
              startTimeLabel={value.startTime}
              endTimeLabel={value.endTime}
              bookingStart={bookingStart}
              bookingEnd={bookingEnd}
              siteHours={siteHours}
              value={value.roomId}
              busyIntervals={busyByRoom}
              conflictedIds={conflictedRoomIds}
              loading={availabilityLoading}
              onChange={(id) => patch({ roomId: id, deviceIds: [] })}
            />
            {onViewRoomAvailability && (
              <Text
                component="button"
                type="button"
                size="sm"
                c="blue"
                style={{
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  textDecoration: 'underline',
                }}
                onClick={onViewRoomAvailability}
              >
                View room availability
              </Text>
            )}
          </Stack>
        )}

        {value.modality === 'telehealth' && value.telehealthFromOffice && (
          <Stack gap="xs">
            <RoomSelect
              rooms={schedulingRooms.filter((r) => r.type === 'phone-booth')}
              eventDate={value.date}
              startTimeLabel={value.startTime}
              endTimeLabel={value.endTime}
              bookingStart={bookingStart}
              bookingEnd={bookingEnd}
              siteHours={siteHours}
              value={value.roomId}
              busyIntervals={busyByRoom}
              conflictedIds={conflictedRoomIds}
              loading={availabilityLoading}
              onChange={(id) => patch({ roomId: id })}
            />
            {onViewRoomAvailability && (
              <Text
                component="button"
                type="button"
                size="sm"
                c="blue"
                style={{
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  textAlign: 'left',
                  textDecoration: 'underline',
                }}
                onClick={onViewRoomAvailability}
              >
                View room availability
              </Text>
            )}
          </Stack>
        )}
        {footer}
      </Stack>
    </Stack>
  );
}
